import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { Logger } from '../utils/logger';
import Config from '../config/config';
import { toastService } from './toastService';
import { translations } from '../i18n';
import type { SupportedLanguage } from '../i18n';

function getT() {
  const lang = (localStorage.getItem('app-language') as SupportedLanguage) || 'fr';
  return translations[lang] ?? translations['fr'];
}

// V4-APICLIENT-INTERCEPTOR 2026-05-29 — Erreurs typées backend → toast variant
/**
 * Erreur API normalisée extraite d'une réponse axios. Forme stable consommable
 * par tous les callers — évite les `error: any` éparpillés dans les hooks.
 *
 * Le backend FastAPI émet typiquement :
 *   - 4xx/5xx → { detail: string | object | [{msg, ...}] }
 *   - error envelope maison → { error: { message, code } }
 *   - error typée structurée → { detail: { error: "<machine_code>", message: "<i18n>", ... } }
 *
 * `code` est le machine code (snake_case) lorsqu'il existe, sinon null.
 * `message` est le texte lisible (déjà localisé côté backend si dispo).
 */
export interface NormalizedApiError {
  code: string | null;
  message: string | null;
  status: number | null;
  detail: unknown;
}

interface DetailObject {
  error?: unknown;
  message?: unknown;
  detail?: unknown;
  [k: string]: unknown;
}

interface ApiErrorBody {
  detail?: unknown;
  error?: { message?: unknown; code?: unknown };
}

/**
 * Extrait un objet d'erreur normalisé depuis une AxiosError (ou toute erreur JS).
 * Tolère les payloads sans structure connue.
 */
export function extractApiError(err: unknown): NormalizedApiError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? null;
    const body = (err.response?.data ?? null) as ApiErrorBody | null;
    const detail = body?.detail;

    // Forme 1 : detail est un objet structuré { error, message, ... }
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const d = detail as DetailObject;
      const code = typeof d.error === 'string' ? d.error : null;
      const message = typeof d.message === 'string' ? d.message : null;
      return { code, message, status, detail };
    }

    // Forme 2 : envelope { error: { message, code } }
    if (body?.error && typeof body.error === 'object') {
      const code = typeof body.error.code === 'string' ? body.error.code : null;
      const message = typeof body.error.message === 'string' ? body.error.message : null;
      return { code, message, status, detail: body.error };
    }

    // Forme 3 : detail string brut (FastAPI defaut)
    if (typeof detail === 'string') {
      return { code: null, message: detail, status, detail };
    }

    // Fallback : message axios
    return { code: null, message: err.message || null, status, detail: body };
  }

  if (err instanceof Error) {
    return { code: null, message: err.message, status: null, detail: null };
  }

  return { code: null, message: null, status: null, detail: null };
}

/**
 * Métadonnées optionnelles passables via `apiClient.get(url, { metadata: { silent: true } })`
 * pour skip le toast d'erreur — utile quand le caller gère lui-même l'UI d'erreur
 * (ex: SOURCES-03 inline validation, polling silencieux).
 */
export interface RequestMetadata {
  silent?: boolean;
}

// Extension du type axios config pour la métadonnée custom
declare module 'axios' {

  export interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
  }

  export interface AxiosRequestConfig {
    metadata?: RequestMetadata;
  }
}

const API_URL = Config.getInstance().apiUrl;

// Custom event name for auth state changes
export const AUTH_LOGOUT_EVENT = 'raiso:auth:logout';

// JWT helpers — centralisés dans jwtUtils.ts
import { getTokenExpirationMs } from '../utils/jwtUtils';
export { getTokenExpirationMs };

// Éviter une dépendance circulaire (authService importe apiClient).
// On utilise un getter lazy : authService s'enregistre après son initialisation.
type AuthServiceRef = {
  getToken(): string | null;
  logout(): void;
  // V4-AUTH-401-FORCE-LOGOUT — refresh silencieux optionnel (cf. authService).
  refreshSession?(): Promise<boolean>;
} | null;
let _authService: AuthServiceRef = null;
export function registerAuthService(svc: NonNullable<AuthServiceRef>) {
  _authService = svc;
}

// V4-AUTH-401-FORCE-LOGOUT — single-flight : une rafale de requêtes qui 401
// simultanément ne déclenche qu'UN seul appel /auth/refresh, partagé par toutes.
let _refreshPromise: Promise<boolean> | null = null;

// Endpoints d'auth : un 401 y est définitif (mauvais identifiants, refresh token
// expiré/absent) et géré par l'appelant (formulaire login, refreshSession). Ne
// PAS tenter de refresh ni de logout en cascade dessus (sinon boucle infinie).
function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return url.includes('/auth/refresh') || url.includes('/auth/token') || url.includes('/auth/logout');
}

// Dispatch logout event so AuthContext can update state
function dispatchLogoutEvent(reason: 'expired' | 'unauthorized' | 'manual' | 'account_inactive') {
  Logger.info('[ApiClient] Dispatching logout event', { reason });
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason } }));
}

// Force logout and dispatch event
export function forceLogout(reason: 'expired' | 'unauthorized' | 'manual' = 'manual') {
  _authService?.logout();
  dispatchLogoutEvent(reason);
}

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true, // Envoyer le cookie httpOnly automatiquement
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — ajoute l'Authorization header depuis le token en mémoire si disponible.
// En l'absence de token en mémoire (ex: après refresh de page), le cookie httpOnly est envoyé
// automatiquement par le navigateur grâce à withCredentials: true.
apiClient.interceptors.request.use(
  (config) => {
    Logger.debug('[ApiClient] HTTP request started', {
      method: config.method?.toUpperCase() ?? 'GET',
      url: config.url ?? '',
    });
    const token = _authService?.getToken() ?? null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Detect popup window — popups share localStorage but must NOT clear
// the auth token or dispatch logout events (the main window handles auth).
const _isPopupWindow = !!window.opener && window.opener !== window;

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => {
    const payload = {
      method: response.config.method?.toUpperCase() ?? 'GET',
      url: response.config.url ?? '',
      status: response.status,
    };

    if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(payload.method)) {
      Logger.info('[ApiClient] HTTP mutation succeeded', payload);
    } else {
      Logger.debug('[ApiClient] HTTP request succeeded', payload);
    }

    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status ?? null;
    const payload = {
      method: error.config?.method?.toUpperCase() ?? 'UNKNOWN',
      url: error.config?.url ?? '',
      status,
      error: error.message,
    };

    // V4-APICLIENT-INTERCEPTOR 2026-05-29 — opt-out toast via config.metadata.silent
    // Permet aux callers (ex: SOURCES-03 inline validation, polling silencieux,
    // hooks qui gèrent eux-mêmes l'UI d'erreur) de skip l'affichage toast.
    const cfg = error.config as InternalAxiosRequestConfig | undefined;
    const silent = cfg?.metadata?.silent === true;

    // Extraction normalisée — utilisable par les callers via extractApiError(err)
    const normalized = extractApiError(error);

    // V4-AUTH-401-FORCE-LOGOUT (2026-07-03) — Auth resilience.
    // Point 1 du TODO historique : une erreur RÉSEAU (timeout ECONNABORTED, ou
    // pas de réponse HTTP du tout — backend en cours de redémarrage, 502/504)
    // ne doit JAMAIS déclencher de logout. `status` est null dans ce cas, donc le
    // bloc 401 ci-dessous ne s'exécute pas ; on log juste pour le debug et on
    // laisse la suite (toast générique) gérer.
    const isNetworkError = !error.response;
    if (isNetworkError) {
      Logger.warn('[ApiClient] Network/timeout error (no HTTP response) — NOT logging out', {
        ...payload,
        code: error.code ?? null,
      });
    }

    const cfg2 = cfg as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (status === 401 && !_isPopupWindow) {
      // Un 401 sur un endpoint d'auth est définitif et géré par l'appelant — pas
      // de refresh ni de logout en cascade ici (évite la boucle infinie).
      if (isAuthEndpoint(cfg2?.url)) {
        Logger.warn('[ApiClient] 401 on auth endpoint — rejecting without cascade logout', payload);
        return Promise.reject(error);
      }

      // Point 2 du TODO : sur un 401 applicatif, tenter d'abord UN refresh
      // silencieux (single-flight) via le cookie refresh_token avant de conclure
      // à une vraie expiration. La majorité des 401 sont transitoires (cookie
      // access_token brièvement expiré, clock skew, backend redémarré).
      if (cfg2 && !cfg2._retry && _authService?.refreshSession) {
        if (!_refreshPromise) {
          _refreshPromise = _authService.refreshSession().finally(() => { _refreshPromise = null; });
        }
        const refreshed = await _refreshPromise;
        if (refreshed) {
          cfg2._retry = true;
          Logger.info('[ApiClient] 401 recovered via silent token refresh, retrying request', payload);
          return apiClient(cfg2);
        }
      }

      // Refresh indisponible ou échoué → vraie expiration de session → logout.
      Logger.warn('[ApiClient] 401 unauthorized — session expired (refresh failed), logging out', payload);
      const hadActiveSession = !!_authService?.getToken();
      _authService?.logout();
      if (hadActiveSession) {
        dispatchLogoutEvent('unauthorized');
      }
      // 401 → no toast (login flow s'en charge)
      return Promise.reject(error);
    }

    if (status === 401 || status === 404) {
      Logger.warn('[ApiClient] HTTP request returned handled warning status', payload);
      return Promise.reject(error);
    }

    // V4-APICLIENT-INTERCEPTOR 2026-05-29 — mapping erreurs typées backend
    // Le backend (cf. AGENTS.md "Error surface design") émet detail.error = machine code
    // pour les erreurs domaine. On mappe vers la bonne variant de toast + message i18n.
    if (silent) {
      Logger.warn('[ApiClient] silent request error (toast skipped)', { ...payload, code: normalized.code });
      return Promise.reject(error);
    }

    const tErrors = getT().errors;

    if (normalized.code) {
      // Erreur typée par le backend — message backend déjà localisé si dispo,
      // sinon fallback i18n par machine code.
      Logger.warn('[ApiClient] typed backend error', { ...payload, code: normalized.code });
      switch (normalized.code) {
        case 'account_inactive': {
          // Compte désactivé / accès révoqué (ex: guest dont le siège a été révoqué).
          // Le backend renvoie 403 → l'intercepteur 401 ne déclenche PAS de logout, d'où
          // une boucle de toasts « accès refusé » sans déconnexion. On force le logout +
          // un événement dédié pour afficher « accès révoqué » (V4-AGENCY-GUEST-REVOKE-MSG).
          Logger.warn('[ApiClient] account inactive — forcing logout', payload);
          const hadInactiveSession = !!_authService?.getToken();
          _authService?.logout();
          if (hadInactiveSession) {
            dispatchLogoutEvent('account_inactive');
          }
          return Promise.reject(error);
        }
        case 'url_scraping_failed':
          toastService.info(
            tErrors.urlScrapingFailed ?? 'Scraping failed',
            normalized.message ?? tErrors.urlScrapingFailedDescription
          );
          return Promise.reject(error);
        case 'llm_worker_unavailable':
          toastService.warning(
            tErrors.llmWorkerUnavailable ?? 'AI worker unavailable',
            normalized.message ?? tErrors.llmWorkerUnavailableDescription
          );
          return Promise.reject(error);
        case 'recursion_limit_exceeded':
          toastService.error(
            tErrors.recursionLimitExceeded ?? 'Recursion limit exceeded',
            normalized.message ?? tErrors.recursionLimitExceededDescription
          );
          return Promise.reject(error);
        case 'agency_permissions_unavailable':
          // 503 backend : les droits agence n'ont pas pu être RÉSOLUS (ES KO), ce
          // n'est pas un refus. Sans ce case, le `default` ci-dessous affichait le
          // message backend en titre et le code machine snake_case en description —
          // lisible pour un dev, pas pour un utilisateur. Warning et non error : la
          // situation est transitoire et l'action est « réessayez ».
          toastService.warning(
            tErrors.agencyPermissionsUnavailable ?? 'Permissions could not be checked',
            normalized.message ?? tErrors.agencyPermissionsUnavailableDescription
          );
          return Promise.reject(error);
        case 'upstream_llm_timeout':
          toastService.error(
            tErrors.upstreamLlmTimeout ?? 'Upstream LLM timeout',
            normalized.message ?? tErrors.upstreamLlmTimeoutDescription
          );
          return Promise.reject(error);
        default:
          // Code inconnu mais structuré → warning + message backend
          toastService.warning(
            normalized.message ?? tErrors.serverError,
            normalized.code ?? undefined
          );
          return Promise.reject(error);
      }
    }

    if (status === 403) {
      Logger.warn('[ApiClient] 403 access denied', payload);
      toastService.error(tErrors.forbidden, tErrors.forbiddenDescription);
    } else if (status === 429) {
      Logger.warn('[ApiClient] 429 rate limit exceeded', payload);
      toastService.warning(tErrors.tooManyRequests, tErrors.tooManyRequestsDescription);
    } else if (status && status >= 500) {
      Logger.error('[ApiClient] HTTP request failed with server error', payload);
      const message =
        (typeof normalized.message === 'string' && normalized.message) ||
        tErrors.serverErrorDefault;
      toastService.error(tErrors.serverError, message);
    } else if (status && status >= 400 && status < 500) {
      // 4xx générique (sauf 401/403/404/429 déjà gérés) → warning avec détail brut si exploitable
      Logger.warn('[ApiClient] 4xx client error', payload);
      const description =
        (typeof normalized.message === 'string' && normalized.message) ||
        tErrors.badRequestDescription;
      toastService.warning(tErrors.badRequest, description);
    } else {
      Logger.error('[ApiClient] HTTP request failed', payload);
    }
    return Promise.reject(error);
  }
);
